/**
 * API Key management methods
 */

import { getSession } from '../../../../../core/auth/supabase-auth';
import { getApiBaseUrl } from '../core';
import { TrainingApiResponse } from '../types';

export interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface GenerateApiKeyResponse {
  key: string;
  key_id: string;
  name: string;
  prefix: string;
  expires_at: string | null;
}

export async function generateApiKey(
  sessionId: string,
  name: string,
  expiresInDays?: number | null
): Promise<TrainingApiResponse<GenerateApiKeyResponse>> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(`${getApiBaseUrl()}/api-keys/${sessionId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ name, expires_in_days: expiresInDays ?? null })
  });

  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data.error || 'Failed to generate API key') as Error & { code?: string };
    err.code = data.code;
    throw err;
  }
  return data;
}

export async function listApiKeys(
  sessionId: string
): Promise<TrainingApiResponse<{ keys: ApiKeyInfo[] }>> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(`${getApiBaseUrl()}/api-keys/${sessionId}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to list API keys');
  return data;
}

export async function revokeApiKey(
  keyId: string
): Promise<TrainingApiResponse> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(`${getApiBaseUrl()}/api-keys/${keyId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to revoke API key');
  return data;
}
